import { useState, useCallback } from "react";
import { AxiosError } from "axios";
import { useAppSnackbar } from "./useAppSnackbar";
import {
  Response,
  vendorDelete,
  vendorUpdate,
  vendorDNSSUpdate,
  vendorStatusUpdate,
  VendorUpdatePayload,
  VendorUpdateValues,
} from "../api/api";

// =============================================================================
// TYPES
// =============================================================================

export type VendorActionKey = "update" | "delete" | "dnss" | "toggleStatus";

export type VendorPayload = {
  vendorKey: string;
  customerVendorId: string;
  vendorName: string;
  updateData?: VendorUpdateValues;
  gpcSignal?: boolean;
  isAccountEnabled?: boolean;
};

export type VendorActionResponse = {
  status: number;
  data?: Response<unknown>;
};

export type CurrentVendorAction = {
  key: VendorActionKey;
  label: string;
  description: string;
  confirmText: string;
} | null;

/** Form fields for vendor update dialog */
export type VendorUpdateFormFields = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  birthMonth: string;
  birthDay: string;
};

/** Vendor result structure from API responses */
type VendorResult = {
  vendorKey?: string;
  httpCode?: number;
  message?: string;
  error?: string;
};

/** Shape for errored items from 206 responses */
type ErrorItem = 
  | { id?: string; name?: string; error?: string; vendorKey?: string; [key: string]: unknown }
  | string;

// =============================================================================
// ACTION CONFIGURATIONS
// =============================================================================

const getActionConfig = (vendorName: string, isAccountEnabled?: boolean) => ({
  update: {
    key: "update" as const,
    label: `Update ${vendorName}`,
    description: `Are you sure you want to update this ${vendorName} account? This will save all changes.`,
    confirmText: "Update",
  },
  delete: {
    key: "delete" as const,
    label: `Delete ${vendorName}`,
    description: `WARNING: Are you sure you want to delete this ${vendorName} account? This action cannot be undone.`,
    confirmText: "Delete",
  },
  dnss: {
    key: "dnss" as const,
    label: `DNSS`,
    description: `Are you sure you want to apply DNSS (Do Not Sell/Share) to this ${vendorName} account?`,
    confirmText: "Apply DNSS",
  },
  toggleStatus: {
    key: "toggleStatus" as const,
    label: isAccountEnabled ? "Disable Account" : "Enable Account",
    description: isAccountEnabled 
      ? `Are you sure you want to disable this ${vendorName} account? The user will no longer be able to access their account.`
      : `Are you sure you want to enable this ${vendorName} account? The user will be able to access their account again.`,
    confirmText: isAccountEnabled ? "Disable" : "Enable",
  },
});

// =============================================================================
// API FUNCTIONS
// =============================================================================

async function updateVendorApi(payload: VendorPayload): Promise<VendorActionResponse> {
  if (!payload.vendorKey || !payload.customerVendorId) {
    return {
      status: 400,
      data: { status: 400, value: null, error: "Missing vendor key or ID", message: null, refreshToken: null },
    };
  }
  // Wrap updateData in the expected updateValues structure
  const requestBody: VendorUpdatePayload = {
    updateValues: payload.updateData ?? {},
  };
  const data = await vendorUpdate(payload.vendorKey, payload.customerVendorId, requestBody);
  return { status: data.status ?? 200, data };
}

async function deleteVendorApi(payload: VendorPayload): Promise<VendorActionResponse> {
  if (!payload.vendorKey || !payload.customerVendorId) {
    return {
      status: 400,
      data: { status: 400, value: null, error: "Missing vendor key or ID", message: null, refreshToken: null },
    };
  }
  const data = await vendorDelete(payload.vendorKey, payload.customerVendorId);
  return { status: data.status ?? 200, data };
}

async function dnssVendorApi(payload: VendorPayload): Promise<VendorActionResponse> {
  if (!payload.vendorKey || !payload.customerVendorId) {
    return {
      status: 400,
      data: { status: 400, value: null, error: "Missing vendor key or ID", message: null, refreshToken: null },
    };
  }
  const data = await vendorDNSSUpdate(payload.vendorKey, payload.customerVendorId, { 
    gpcSignal: payload.gpcSignal ?? true 
  });
  return { status: data.status ?? 200, data };
}

async function toggleStatusVendorApi(payload: VendorPayload): Promise<VendorActionResponse> {
  if (!payload.vendorKey || !payload.customerVendorId) {
    return {
      status: 400,
      data: { status: 400, value: null, error: "Missing vendor key or ID", message: null, refreshToken: null },
    };
  }
  // If currently enabled, send true to disable; if currently disabled, send false to enable
  const statusToSend = payload.isAccountEnabled;
  const data = await vendorStatusUpdate(payload.vendorKey, payload.customerVendorId, { 
    status: statusToSend ?? false 
  });
  return { status: data.status ?? 200, data };
}

const VENDOR_CONFIRM_APIS: Record<
  VendorActionKey,
  (payload: VendorPayload) => Promise<VendorActionResponse>
> = {
  update: updateVendorApi,
  delete: deleteVendorApi,
  dnss: dnssVendorApi,
  toggleStatus: toggleStatusVendorApi,
};

function vendorConfirmSuccessMessage(
  actionKey: VendorActionKey,
  configLabel: string,
  isAccountEnabled: boolean | undefined,
  branch: "ok200" | "else"
): string {
  if (actionKey === "delete") {
    return "Account deletion has been successfully queued.";
  }
  if (actionKey === "dnss") {
    return "DNSS has been successfully applied.";
  }
  if (actionKey === "toggleStatus") {
    return isAccountEnabled
      ? "Account has been successfully disabled."
      : "Account has been successfully enabled.";
  }
  return branch === "ok200"
    ? `${configLabel} completed successfully!`
    : "Action completed successfully!";
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function isAxiosError(error: unknown): error is AxiosError<Response<unknown>> {
  return (error as AxiosError).isAxiosError === true;
}

function getStatusFromError(error: unknown): number {
  if (isAxiosError(error) && error.response) { 
    return error.response.status; 
  }
  return 500;
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const responseData = error.response?.data;
    if (responseData?.error) { return responseData.error; }
    if (responseData?.message) { return String(responseData.message); }
    return error.message;
  }
  if (error instanceof Error) { return error.message; }
  return "An unknown error occurred";
}

function hasNoErrorMessage(data: unknown): boolean {
  const response = data as Response<unknown> | undefined;
  if (response?.error) { return false; }
  if (response?.message && typeof response.message === "string") {
    return !response.message.toLowerCase().includes("error");
  }
  return true;
}

/** Handle specific HTTP status codes with user-friendly messages */
function handleErrorStatus(
  status: number, 
  label: string, 
  errorMessage: string, 
  showSnackbar: (msg: string, sev: "error") => void
) {
  const messages: Record<number, string> = {
    400: `Unable to complete ${label.toLowerCase()}. Please check your information and try again.`,
    401: "Your session has expired. Please log in again to continue.",
    403: "You don't have permission to perform this action. Please contact your administrator if you believe this is an error.",
    404: `The account or resource could not be found. It may have been deleted or moved.`,
    408: `The request took too long to complete. Please check your connection and try again.`,
    409: `Unable to complete ${label.toLowerCase()} due to a conflict. The data may have been modified by another user.`,
    422: `The information provided is invalid. Please review your entries and try again.`,
    429: "Too many requests. Please wait a moment before trying again.",
    500: "We encountered an unexpected error. Our team has been notified. Please try again later.",
    502: "The service is temporarily unavailable. Please try again in a few minutes.",
    503: "The service is currently undergoing maintenance. Please try again shortly.",
    504: "The request timed out. The server is taking too long to respond. Please try again later.",
  };
  
  // Use specific message if available, otherwise show a friendly generic message
  const userMessage = messages[status] ?? 
    `We couldn't complete the ${label.toLowerCase()}. Please try again or contact support if the problem persists.`;
  
  showSnackbar(userMessage, "error");
}

/** Parse vendor failures from API response - extracts vendors that failed (non-200 status) */
function parseFailedVendors(data: unknown): string[] {
  const response = data as { 
    errors?: ErrorItem[]; 
    value?: { errors?: ErrorItem[] } | VendorResult[];
    vendorResults?: VendorResult[];
  } | VendorResult[] | undefined;
  
  const failedVendors: string[] = [];
  
  // Check if response is an array of vendor results
  if (Array.isArray(response)) {
    response
      .filter((item: VendorResult) => item.httpCode && item.httpCode !== 200)
      .forEach((item: VendorResult) => {
        failedVendors.push(item.vendorKey ?? "Unknown");
      });
    return failedVendors;
  }
  
  // Check if vendorResults exists at top level
  if (response?.vendorResults && Array.isArray(response.vendorResults)) {
    response.vendorResults
      .filter((item: VendorResult) => item.httpCode && item.httpCode !== 200)
      .forEach((item: VendorResult) => {
        failedVendors.push(item.vendorKey ?? "Unknown");
      });
    return failedVendors;
  }
  
  // Check if value contains vendor results
  if (response?.value && Array.isArray(response.value)) {
    response.value
      .filter(
        (item): item is VendorResult =>
          item !== null &&
          typeof item === "object" &&
          "httpCode" in item &&
          typeof item.httpCode === "number"
      )
      .filter((item) => item.httpCode !== 200)
      .forEach((item) => {
        failedVendors.push(item.vendorKey ?? "Unknown");
      });
    return failedVendors;
  }
  
  // Fallback to legacy error parsing
  const errors = response?.errors ?? (response?.value as { errors?: ErrorItem[] })?.errors ?? [];
  errors.forEach((item) => {
    if (typeof item === "string") { 
      failedVendors.push(item);
    } else {
      failedVendors.push(item.vendorKey ?? item.name ?? item.id ?? "Unknown");
    }
  });
  
  return failedVendors;
}

/** Build user-friendly 206 message with failed vendors */
function build206Message(actionType: VendorActionKey, data: unknown): string {
  const failedVendors = parseFailedVendors(data);
  
  let baseMessage: string;
  switch (actionType) {
    case "delete":
      baseMessage = "Account deletion successfully queued";
      break;
    case "dnss":
      baseMessage = "DNSS applied successfully";
      break;
    case "toggleStatus":
      baseMessage = "Account status updated successfully";
      break;
    default:
      baseMessage = "Update completed successfully";
  }
  
  if (failedVendors.length === 0) {
    return `${baseMessage}, but some items may have had issues.`;
  }
  
  const vendorList = failedVendors.join(", ");
  return `${baseMessage}, but the following vendors failed: ${vendorList}`;
}

// =============================================================================
// HOOK
// =============================================================================

export type UseVendorActionsOptions = {
  vendorKey: string;
  customerVendorId: string;
  vendorName: string;
  /** Whether the account is currently enabled (for toggle status) */
  isAccountEnabled?: boolean;
  getUpdateData?: () => VendorUpdateValues;
  onSuccess?: (actionKey: VendorActionKey) => void | Promise<void>;
  onFailure?: (actionKey: VendorActionKey, data: unknown) => void;
};

export type UseVendorActionsReturn = {
  dialogOpen: boolean;
  updateDialogOpen: boolean;
  currentAction: CurrentVendorAction;
  loading: boolean;
  openDialog: (actionKey: VendorActionKey) => void;
  closeDialog: () => void;
  closeUpdateDialog: () => void;
  confirmAction: () => Promise<void>;
  confirmUpdate: (formFields: VendorUpdateFormFields) => Promise<void>;
};

export const useVendorActions = ({
  vendorKey,
  customerVendorId,
  vendorName,
  isAccountEnabled,
  getUpdateData,
  onSuccess,
  onFailure,
}: UseVendorActionsOptions): UseVendorActionsReturn => {
  const { showSnackbar } = useAppSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [currentActionKey, setCurrentActionKey] = useState<VendorActionKey | null>(null);
  const [loading, setLoading] = useState(false);

  const actionConfigs = getActionConfig(vendorName, isAccountEnabled);

  const currentAction: CurrentVendorAction = currentActionKey
    ? actionConfigs[currentActionKey]
    : null;

  const openDialog = useCallback((actionKey: VendorActionKey) => {
    setCurrentActionKey(actionKey);
    if (actionKey === "update") {
      setUpdateDialogOpen(true);
    } else {
      setDialogOpen(true);
    }
  }, []);

  const closeDialog = useCallback(() => {
    if (loading) { return; }
    setDialogOpen(false);
    setCurrentActionKey(null);
  }, [loading]);

  const closeUpdateDialog = useCallback(() => {
    if (loading) { return; }
    setUpdateDialogOpen(false);
    setCurrentActionKey(null);
  }, [loading]);

  /**
   * Confirm update with form fields from the VendorUpdateDialog
   */
  const confirmUpdate = useCallback(async (formFields: VendorUpdateFormFields) => {
    if (loading) { return; }

    const config = actionConfigs.update;
    setLoading(true);

    // Build the update values matching the expected API format
    const updateValues: VendorUpdateValues = {
      firstName: formFields.firstName || undefined,
      lastName: formFields.lastName || undefined,
      email: formFields.email || undefined,
      mobilePhoneNumber: formFields.phoneNumber || undefined,
    };

    // Add birthday in ISO format with birthdayUpdated flag if provided
    if (formFields.birthMonth && formFields.birthDay) {
      // Format as ISO date string with default year 1900
      updateValues.birthday = `1900-${formFields.birthMonth}-${formFields.birthDay}T00:00:00.000Z`;
      updateValues.birthdayUpdated = true;
    }

    const payload: VendorPayload = {
      vendorKey,
      customerVendorId,
      vendorName,
      updateData: updateValues,
    };

    try {
      const response = await updateVendorApi(payload);
      const { status, data } = response;

      if (status === 200 && hasNoErrorMessage(data)) {
        showSnackbar(`Account updated successfully! Changes have been saved.`, "success");
        await onSuccess?.("update");
      } else if (status === 206) {
        const partialMessage = build206Message("update", data);
        showSnackbar(partialMessage, "warning");
        await onSuccess?.("update");
      } else if (status >= 400) {
        handleErrorStatus(status, config.label, getErrorMessage({ response: { data } }), showSnackbar);
        onFailure?.("update", data);
      } else {
        showSnackbar(`Changes saved successfully!`, "success");
        await onSuccess?.("update");
      }
    } catch (error) {
      const status = getStatusFromError(error);
      handleErrorStatus(status, "Update", getErrorMessage(error), showSnackbar);
      onFailure?.("update", { error });
    } finally {
      setLoading(false);
      setUpdateDialogOpen(false);
      setCurrentActionKey(null);
    }
  }, [
    loading,
    actionConfigs,
    vendorKey,
    customerVendorId,
    vendorName,
    showSnackbar,
    onSuccess,
    onFailure,
  ]);

  const confirmAction = useCallback(async () => {
    if (!currentActionKey || loading) { return; }

    const config = actionConfigs[currentActionKey];
    setLoading(true);

    const payload: VendorPayload = {
      vendorKey,
      customerVendorId,
      vendorName,
      updateData: currentActionKey === "update" ? getUpdateData?.() : undefined,
      gpcSignal: currentActionKey === "dnss" ? true : undefined,
      isAccountEnabled: currentActionKey === "toggleStatus" ? isAccountEnabled : undefined,
    };

    try {
      const apiCall = VENDOR_CONFIRM_APIS[currentActionKey] ?? deleteVendorApi;
      const response = await apiCall(payload);
      const { status, data } = response;

      if (status === 200 && hasNoErrorMessage(data)) {
        showSnackbar(
          vendorConfirmSuccessMessage(currentActionKey, config.label, isAccountEnabled, "ok200"),
          "success"
        );
        await onSuccess?.(currentActionKey);
      } else if (status === 206) {
        const partialMessage = build206Message(currentActionKey, data);
        showSnackbar(partialMessage, "warning");
        await onSuccess?.(currentActionKey);
      } else if (status >= 400) {
        handleErrorStatus(status, config.label, getErrorMessage({ response: { data } }), showSnackbar);
        onFailure?.(currentActionKey, data);
      } else {
        showSnackbar(
          vendorConfirmSuccessMessage(currentActionKey, config.label, isAccountEnabled, "else"),
          "success"
        );
        await onSuccess?.(currentActionKey);
      }
    } catch (error) {
      const status = getStatusFromError(error);
      const actionLabel = currentActionKey === "delete" ? "Delete" : config.label;
      handleErrorStatus(status, actionLabel, getErrorMessage(error), showSnackbar);
      onFailure?.(currentActionKey, { error });
    } finally {
      setLoading(false);
      setDialogOpen(false);
      setCurrentActionKey(null);
    }
  }, [
    currentActionKey,
    loading,
    actionConfigs,
    vendorKey,
    customerVendorId,
    vendorName,
    isAccountEnabled,
    getUpdateData,
    showSnackbar,
    onSuccess,
    onFailure,
  ]);

  return {
    dialogOpen,
    updateDialogOpen,
    currentAction,
    loading,
    openDialog,
    closeDialog,
    closeUpdateDialog,
    confirmAction,
    confirmUpdate,
  };
};

export default useVendorActions;
